import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useLang } from "@/contexts/LangContext";
import { speciesLabel } from "@/lib/patientUtils";

/**
 * Type-to-search patient combobox. Replaces the long <select> on Appointments/Invoices
 * for clinics with hundreds of records.
 *
 * Props:
 *   patients       — array of patient objects: { id, name, owner: { full_name, phone } }
 *   value          — currently selected patient id (string)
 *   onChange(id)   — called when a suggestion is picked (or cleared)
 *   testId         — data-testid prefix
 *   placeholder    — input placeholder
 *   required       — HTML5 required flag on the underlying hidden input
 */
export default function PatientSearchSelect({
  patients = [],
  value = "",
  onChange,
  testId = "patient-search",
  placeholder = "Caută pacient sau proprietar…",
  required = false,
}) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => patients.find((p) => p.id === value) || null,
    [patients, value]
  );

  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return patients.slice(0, 20);
    return patients
      .filter((p) => {
        const hay = [
          p.name,
          p.owner?.full_name,
          p.owner?.phone,
          p.owner?.email,
          p.species,
          p.breed,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      })
      .slice(0, 30);
  }, [q, patients]);

  function pick(p) {
    onChange && onChange(p ? p.id : "");
    setQ("");
    setOpen(false);
  }

  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) pick(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      {selected && !open ? (
        <div className="flex items-center gap-2 pf-input !py-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-pf-text truncate">
              {selected.name}
            </div>
            {selected.owner?.full_name && (
              <div className="text-xs text-pf-text-secondary truncate">
                {selected.owner.full_name}
                {selected.owner?.phone ? ` · ${selected.owner.phone}` : ""}
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => pick(null)}
            className="text-pf-text-secondary hover:text-pf-danger"
            title="Change"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            data-testid={`${testId}-edit`}
            onClick={() => {
              setOpen(true);
              setQ("");
            }}
            className="text-xs text-pf-primary hover:underline"
          >
            Schimbă
          </button>
        </div>
      ) : (
        <div className="relative">
          <MagnifyingGlass
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-pf-text-secondary pointer-events-none"
          />
          <input
            data-testid={`${testId}-input`}
            className="pf-input pl-8"
            value={q}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setActiveIdx(0);
            }}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
        </div>
      )}

      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-y-auto pf-card p-1 bg-white shadow-dropdown"
          data-testid={`${testId}-list`}
        >
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-pf-text-secondary">
              Niciun pacient găsit.
            </div>
          ) : (
            results.map((p, i) => (
              <button
                key={p.id}
                type="button"
                data-testid={`${testId}-item-${p.id}`}
                onClick={() => pick(p)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left rounded-md px-2.5 py-2 flex items-center gap-2 transition-colors ${
                  i === activeIdx ? "bg-pf-primary-light" : "hover:bg-pf-bg"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-pf-text truncate">
                    {p.name}
                  </div>
                  <div className="text-xs text-pf-text-secondary truncate">
                    {p.owner?.full_name || "—"}
                    {p.owner?.phone ? ` · ${p.owner.phone}` : ""}
                    {p.species ? ` · ${speciesLabel(p.species, t)}` : ""}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Native required validation shim */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          required
          value={value || ""}
          onChange={() => {}}
        />
      )}
    </div>
  );
}
